// FractionCalculator.js -------------------------------------------------
// A reusable interactive for mixed‑number fraction operations in Grade 8.
// Operation buttons are displayed in a column to the left of the inputs.
// Clicking +, −, ×, or ÷ immediately computes the result.
// ----------------------------------------------------------------------------
// • Inputs: numerator & denominator with mixed‑number support (whole + n/d)
// • Operations: +  −  ×  ÷ (vertical column)
// • Outputs: improper fraction (simplified), mixed number, decimal
// ----------------------------------------------------------------------------
// Usage: import FractionCalculator from "../FractionCalculator";
// and include <FractionCalculator /> in your React app.

import React, { useState } from "react";

// Euclidean algorithm for GCD
function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b !== 0) {
    [a, b] = [b, a % b];
  }
  return a || 1;
}

// Simplify a fraction {n, d}
function simplify({ n, d }) {
  const g = gcd(n, d);
  return { n: n / g, d: d / g };
}

// Convert mixed‑number {w, n, d} to improper {n, d}
function toImproper({ w = 0, n, d }) {
  const numerator = (Math.abs(w) * d + n) * (w < 0 ? -1 : 1);
  return { n: numerator, d };
}

// Convert improper {n, d} to mixed‑number {w, n, d}
function toMixed({ n, d }) {
  const w = Math.trunc(n / d);
  const rem = Math.abs(n % d);
  return { w, n: rem, d };
}

export default function FractionCalculator() {
  // State for fraction A
  const [aWhole, setAWhole] = useState(0);
  const [aNum,   setANum]   = useState(1);
  const [aDen,   setADen]   = useState(1);

  // State for fraction B
  const [bWhole, setBWhole] = useState(0);
  const [bNum,   setBNum]   = useState(1);
  const [bDen,   setBDen]   = useState(1);

  // Current operation and result
  const [op,     setOp]     = useState("+");
  const [result, setResult] = useState(null);

  // Compute result for a given operation
  function compute(selectedOp) {
    if (aDen === 0 || bDen === 0) {
      alert("Denominator cannot be zero");
      return;
    }
    const A = toImproper({ w: +aWhole, n: +aNum, d: +aDen });
    const B = toImproper({ w: +bWhole, n: +bNum, d: +bDen });
    let n = 0, d = 1;
    switch (selectedOp) {
      case "+": n = A.n * B.d + B.n * A.d; d = A.d * B.d; break;
      case "-": n = A.n * B.d - B.n * A.d; d = A.d * B.d; break;
      case "×": n = A.n * B.n;             d = A.d * B.d; break;
      case "÷": n = A.n * B.d;             d = A.d * B.n; if (d === 0) { alert("Division by zero"); return; } break;
      default: return;
    }
    setResult(simplify({ n, d }));
  }

  // Sub‑component for mixed‑number inputs
  function FractionInputs({ label, w, n, d, setW, setN, setD }) {
    return (
      <div style={{ marginBottom: 12 }}>
        <div style={{ marginBottom: 4, fontWeight: 600 }}>{label}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Whole part */}
          <input
            type="number"
            value={w}
            onChange={e => setW(e.target.value)}
            placeholder="whole"
            style={{ width: 60, padding: 4, borderRadius: 4, border: '1px solid #ccc' }}
          />
          {/* Vertical fraction */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <input
              type="number"
              value={n}
              onChange={e => setN(e.target.value)}
              placeholder="n"
              style={{ width: 60, padding: 4, border: '1px solid #ccc', borderBottom: 0, borderTopLeftRadius: 4, borderTopRightRadius: 4 }}
            />
            <div style={{ width: 60, borderTop: '1px solid #000' }} />
            <input
              type="number"
              value={d}
              onChange={e => setD(e.target.value)}
              placeholder="d"
              style={{ width: 60, padding: 4, border: '1px solid #ccc', borderTop: 0, borderBottomLeftRadius: 4, borderBottomRightRadius: 4 }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 400, margin: '0 auto', padding: 16, background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <h3 style={{ fontSize: '1.125rem', marginBottom: 16, fontWeight: 'bold' }}>Fraction Calculator</h3>

      <div style={{ display: 'flex', gap: 24 }}>
        {/* Operation buttons column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {['+', '-', '×', '÷'].map(sym => (
            <button
              key={sym}
              onClick={() => { setOp(sym); compute(sym); }}
              style={{
                width: 48,
                padding: '8px 0',
                marginTop: "20px",
                background: op === sym ? '#4caf50' : '#f0f0f0',
                color: op === sym ? '#fff' : '#000',
                border: '1px solid #ccc',
                borderRadius: 4,
                cursor: 'pointer'
              }}
            >
              {sym}
            </button>
          ))}
        </div>

        {/* Fraction inputs side */}
        <div style={{ flex: 1 }}>
          <FractionInputs
            label="A"
            w={aWhole} n={aNum} d={aDen}
            setW={setAWhole} setN={setANum} setD={setADen}
          />
          <FractionInputs
            label="B"
            w={bWhole} n={bNum} d={bDen}
            setW={setBWhole} setN={setBNum} setD={setBDen}
          />
        </div>
      </div>

      {/* Result display */}
      {result && (
        <div style={{ marginTop: 24, paddingTop: 12, borderTop: '1px solid #ddd', fontSize: 14 }}>
          <p><strong>Improper:</strong> {result.n}/{result.d}</p>
          {(() => {
            const m = toMixed(result);
            return <p><strong>Mixed:</strong> {m.w ? `${m.w} ` : ''}{m.n}/{m.d}</p>;
          })()}
          <p><strong>Decimal:</strong> {(result.n / result.d).toFixed(2)}</p>
        </div>
      )}
    </div>
  );
}
